import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { styles } from "./styles";
import { getRequest, postRequest } from "../../../../Apis";
import { useNavigate } from "react-router-dom";
import {
  authEndpoints,
  campaignEndpoints,
} from "../../../../utils/constants/httpConstants";
import Image from "../../../../components/Image/Image";
import {
  EMITTER_ERROR,
  EMITTER_SUCCESS,
  ESP_IMAGES_BY_INDEX,
  ESP_PROVIDER_LABEL,
} from "../../../../utils/constants";
import { NO_RECORD_FOUND_IMAGE } from "../../../../utils/constants/assets";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import { GREY, PRIMARY } from "../../../../utils/constants/colors";
import {
  ICON_LABELS,
  fontSize,
  fontWeight,
} from "../../../../utils/constants/UI";
import { STRINGS } from "../../strings";
import IconComponent from "../../../../components/Icon/IconComponent";
import SkeletonLoader from "../../../../components/Loaders/SkeletonLoader";
import CustomStyledMenu from "../../../../components/Menu/CustomStyledMenu";
import DisconnectModal from "./DisconnectModal";
import ReconnectModal from "./ReconnectModal";
import EditInboxModal from "./EditInboxModal";
import MailboxUsageModal from "./MailboxUsageModal";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { getErrorMessage } from "../../../../utils/helpers";

// `mailbox_status` from get-api-details -> chip copy + tone. Anything not listed
// here still renders (neutral grey, raw value capitalised) so a new backend
// state shows up instead of silently disappearing.
const MAILBOX_STATUS_META = {
  connected: {
    tone: "ok",
    label: STRINGS.MAILBOX_STATUS_CONNECTED,
    hint: STRINGS.MAILBOX_STATUS_CONNECTED_HINT,
  },
  connecting: {
    tone: "pending",
    label: STRINGS.MAILBOX_STATUS_CONNECTING,
    hint: STRINGS.MAILBOX_STATUS_CONNECTING_HINT,
  },
  pending: {
    tone: "pending",
    label: STRINGS.MAILBOX_STATUS_CONNECTING,
    hint: STRINGS.MAILBOX_STATUS_CONNECTING_HINT,
  },
  disconnected: {
    tone: "bad",
    label: STRINGS.MAILBOX_STATUS_DISCONNECTED,
    hint: STRINGS.MAILBOX_STATUS_DISCONNECTED_HINT,
  },
  disconnect: {
    tone: "bad",
    label: STRINGS.MAILBOX_STATUS_DISCONNECTED,
    hint: STRINGS.MAILBOX_STATUS_DISCONNECTED_HINT,
  },
  failed: {
    tone: "bad",
    label: STRINGS.MAILBOX_STATUS_DISCONNECTED,
    hint: STRINGS.MAILBOX_STATUS_DISCONNECTED_HINT,
  },
  error: {
    tone: "bad",
    label: STRINGS.MAILBOX_STATUS_DISCONNECTED,
    hint: STRINGS.MAILBOX_STATUS_DISCONNECTED_HINT,
  },
  expired: {
    tone: "bad",
    label: STRINGS.MAILBOX_STATUS_DISCONNECTED,
    hint: STRINGS.MAILBOX_STATUS_DISCONNECTED_HINT,
  },
  revoked: {
    tone: "bad",
    label: STRINGS.MAILBOX_STATUS_DISCONNECTED,
    hint: STRINGS.MAILBOX_STATUS_DISCONNECTED_HINT,
  },
  invalid: {
    tone: "bad",
    label: STRINGS.MAILBOX_STATUS_DISCONNECTED,
    hint: STRINGS.MAILBOX_STATUS_DISCONNECTED_HINT,
  },
};

// The bad-tone states of MAILBOX_STATUS_META. A row in any of these cannot send,
// so the kebab must offer Connect rather than Disconnect. Kept as one list so the
// chip and the menu can never disagree about what "disconnected" means.
const DISCONNECTED_STATES = [
  "disconnected",
  "disconnect",
  "failed",
  "error",
  "expired",
  "revoked",
  "invalid",
];

const isRowDisconnected = (row) =>
  DISCONNECTED_STATES.includes(
    (row?.mailbox_status ?? row?.status ?? "").toString().trim().toLowerCase()
  );

// Prefer `mailbox_status`; `status` is the older field and still the only one
// some rows (API-key ESPs) carry. Returns null when neither is present.
const resolveMailboxStatus = (item) => {
  const raw = (item?.mailbox_status ?? item?.status ?? "").toString().trim();
  if (!raw) return null;
  const meta = MAILBOX_STATUS_META[raw.toLowerCase()];
  return {
    tone: meta?.tone || "neutral",
    // Unknown states: humanise the raw value ("token_expired" -> "Token Expired"
    // once the chip's text-transform capitalises it).
    label: meta?.label || raw.replace(/[_-]+/g, " ").toLowerCase(),
    hint: meta?.hint || "",
  };
};

// Shared esp -> label map (2: Gmail, 5: Outlook, 8: Zoho). Read at render time
// (not aliased at module load) to stay safe against import-cycle init order.
const ConnectedEspList = () => {
  const classes = styles();
  const navigate = useNavigate();

  const [esp, setEsp] = useState([]);
  const [gmailList, setGmailList] = useState([]);
  const [outlookList, setOutlookList] = useState([]);
  const [zohoList, setZohoList] = useState([]);
  const [isSkeleton, setIsSkeleton] = useState(false);
  const [reload, setReload] = useState(1);

  // Bulk sending-status for every connected mailbox, keyed by email. Fetched
  // once as soon as the Integrations tab loads so the whole tab has live usage
  // data available (View Usage reuses this instead of re-hitting per mailbox).
  const [bulkUsage, setBulkUsage] = useState({});

  const [optionAnchorEl, setOptionAnchorEl] = useState(null);
  const [optionAnchorEl1, setOptionAnchorEl1] = useState(null);
  const [dataRow, setDataRow] = useState({});
  const [disconnect, setDisconnect] = useState(false);
  const [reconnect, setReconnect] = useState(false);
  const [edit, setEdit] = useState(false);
  const [usage, setUsage] = useState(false);

  const optionOpen = Boolean(optionAnchorEl);
  const optionOpen1 = Boolean(optionAnchorEl1);

  useEffect(() => {
    showlist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const showlist = async () => {
    try {
      setIsSkeleton(true);
      const response = await getRequest(navigate, authEndpoints?.getApiKeyDetails);
      const gmail = response?.data?.data?.inboxes?.gmail || [];
      const outlook = response?.data?.data?.inboxes?.outlook || [];
      const zoho = response?.data?.data?.inboxes?.zoho || [];
      const others = response?.data?.data?.other_api_details || [];
      setEsp(others);
      setGmailList(gmail);
      setOutlookList(outlook);
      setZohoList(zoho);

      if (gmail.length > 0 || outlook.length > 0 || zoho.length > 0 || others.length > 0) {
        window.dispatchEvent(new CustomEvent("setup:mailbox-auto-advance"));
      }

      // As soon as the tab loads, hit mailbox-sending-status once with EVERY
      // connected email so the whole Integrations tab has live usage on hand.
      const emails = [
        ...gmail.map((m) => m?.email),
        ...outlook.map((m) => m?.email),
        ...zoho.map((m) => m?.email),
        ...others.map((m) => m?.email || m?.key),
      ].filter(Boolean);
      fetchBulkUsage(emails);
    } catch (e) {
      /* noop */
    } finally {
      setIsSkeleton(false);
    }
  };

  // Fire the bulk sending-status API and stash the result keyed by email.
  const fetchBulkUsage = async (emails) => {
    if (!emails?.length) {
      setBulkUsage({});
      return;
    }
    try {
      const res = await postRequest(
        navigate,
        campaignEndpoints.mailboxSendingStatus,
        { emails },
      );
      // API shape: { data: { mailboxes: [ { email, found, hourly, daily, ... } ] } }
      const payload = res?.data?.data || res?.data || {};
      const arr = Array.isArray(payload)
        ? payload
        : payload?.mailboxes || payload?.results || [];
      const byEmail = {};
      arr.forEach((m) => {
        if (m?.email) byEmail[m.email] = m;
      });
      setBulkUsage(byEmail);
    } catch (err) {
      // Non-blocking: the tab still renders even if usage can't be fetched.
      setBulkUsage({});
    }
  };

  const openOption = (event, row) => {
    event.stopPropagation();
    setOptionAnchorEl(event?.currentTarget);
    setDataRow(row);
  };
  const openOption1 = (event, row) => {
    event.stopPropagation();
    setOptionAnchorEl1(event?.currentTarget);
    setDataRow(row);
  };
  const handleClose = () => {
    setOptionAnchorEl(null);
    setOptionAnchorEl1(null);
  };
  const openDisconnect = () => {
    handleClose();
    setDisconnect((v) => !v);
  };
  const openConnect = () => {
    handleClose();
    setReconnect(true);
  };
  const openEdit = () => {
    handleClose();
    // Limits can only be changed while the mailbox is idle. If it has already
    // sent today (daily.sent > 0), block the edit and tell the user.
    const key = dataRow?.email || dataRow?.key;
    const dailySent = bulkUsage?.[key]?.daily?.sent;
    if (Number(dailySent) > 0) {
      EventEmitter.emit(EMITTER_ERROR, STRINGS.EDIT_BLOCKED_WHILE_SENDING);
      return;
    }
    setEdit((v) => !v);
  };
  const openUsage = () => {
    handleClose();
    setUsage((v) => !v);
  };

  // "Make it Primary" action hidden from the UI (menu option removed).
  // Kept commented for reference in case it needs to be restored.
  // const makePrim = async () => {
  //   try {
  //     const response = await postRequest(navigate, authEndpoints.makePrimary, {
  //       email: dataRow?.email,
  //     });
  //     EventEmitter.emit(EMITTER_SUCCESS, response?.data?.message);
  //     setReload((r) => r + 1);
  //     handleClose();
  //   } catch (err) {
  //     EventEmitter.emit(
  //       EMITTER_ERROR,
  //       getErrorMessage(err?.data?.data?.errors || {}, err?.data?.message),
  //     );
  //   }
  // };

  const statusToneClass = {
    ok: classes.espRowStatusOk,
    pending: classes.espRowStatusPending,
    bad: classes.espRowStatusBad,
    neutral: classes.espRowStatusNeutral,
  };

  const renderStatusChip = (item) => {
    const status = resolveMailboxStatus(item);
    if (!status) return null;
    return (
      <span
        className={`${classes.espRowStatusChip} ${statusToneClass[status.tone]}`}
        title={status.hint || status.label}
      >
        <span className={classes.espRowStatusDot} />
        {status.label}
      </span>
    );
  };

  /**
   * GMAIL SEND-AS ALIAS: choose which address this mailbox sends FROM.
   * Only rendered when Gmail reported verified aliases for it, so every other
   * mailbox (and every non-Gmail provider) looks exactly as it did before.
   *
   * ONE-WAY CHOICE (Aditya 2026-08-12): once an alias is picked it is LOCKED —
   * campaigns stamped with it must never change identity mid-sequence. The
   * dropdown is therefore replaced by a read-only chip after selection, and the
   * only way back is disconnect → reconnect (which resets the mailbox anyway).
   * That is also why selection asks for confirmation first.
   * Receiving is unaffected either way — IMAP always stays on the account.
   */
  const handleAliasChange = async (item, alias) => {
    if (!alias) return; // placeholder re-selected — nothing to do
    const sure = window.confirm(
      `${STRINGS.SENDING_AS_CONFIRM_1} ${alias}.\n\n${STRINGS.SENDING_AS_CONFIRM_2}`
    );
    if (!sure) return;
    try {
      const res = await postRequest(navigate, authEndpoints.setSendAsAlias, {
        email: item?.email,
        alias,
      });
      EventEmitter.emit(EMITTER_SUCCESS, `Now sending as ${alias}`);
      setReload((p) => p + 1);
      return res;
    } catch (err) {
      EventEmitter.emit(EMITTER_ERROR, getErrorMessage(err));
    }
  };

  const renderAliasPicker = (item) => {
    const aliases = item?.available_aliases || [];
    const chosen = item?.send_as_alias;
    if (!chosen && !aliases.length) return null;
    // locked: show the chosen address as a chip, no control to change it
    if (chosen) {
      return (
        <Box className={classes.espRowAlias} title={STRINGS.SENDING_AS_LOCKED_HINT}>
          <span className={classes.espRowAliasLabel}>{STRINGS.SENDING_AS}</span>
          <span className={classes.espRowAliasChip}>
            <IconComponent
              iconLabel={ICON_LABELS.MAIL_LOCK}
              fontSize={fontSize.XS}
              color={GREY.SECONDARY}
            />
            {chosen}
          </span>
        </Box>
      );
    }
    return (
      <Box className={classes.espRowAlias}>
        <span className={classes.espRowAliasLabel}>{STRINGS.SENDING_AS}</span>
        <select
          className={classes.espRowAliasSelect}
          value=""
          onChange={(e) => handleAliasChange(item, e.target.value)}
          title={STRINGS.SENDING_AS_HINT}
        >
          <option value="">{`${item?.email} (default)`}</option>
          {aliases.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
      </Box>
    );
  };

  const renderMailboxRow = (item, providerEsp) => {
    const logoSrc = ESP_IMAGES_BY_INDEX?.[providerEsp];
    const providerLabel = ESP_PROVIDER_LABEL?.[providerEsp] || "Mailbox";
    return (
      <Box className={classes.espRow} key={item?._id || item?.email}>
        <Box className={classes.espRowIdentity}>
          {logoSrc && (
            <Image
              src={logoSrc}
              alt={providerLabel}
              className={classes.espRowLogoImg}
            />
          )}
          <span className={classes.espRowEmail} title={item?.email}>
            {item?.email}
          </span>
          {renderStatusChip(item)}
          {renderAliasPicker(item)}
        </Box>
        <Box className={classes.espRowLimits}>
          <Box className={classes.espRowLimit}>
            <span className={classes.espRowLimitLabel}>{STRINGS.HOURLY}</span>
            <span className={classes.espRowLimitValue}>
              {item?.hourly_limit ?? 0}
            </span>
          </Box>
          <Box className={classes.espRowDivider} />
          <Box className={classes.espRowLimit}>
            <span className={classes.espRowLimitLabel}>{STRINGS.DAILY}</span>
            <span className={classes.espRowLimitValue}>
              {item?.daily_limit ?? 0}
            </span>
          </Box>
        </Box>
        <Box
          className={classes.espRowKebab}
          onClick={(e) => openOption(e, { ...item, esp: item?.esp ?? providerEsp })}
        >
          <IconComponent
            iconLabel={ICON_LABELS.MORE_VERT}
            color={GREY.SECONDARY}
            fontSize={fontSize.MD}
          />
        </Box>
      </Box>
    );
  };

  const renderOtherEspRow = (item) => (
    <Box className={classes.espRow} key={item?.esp}>
      <Box className={classes.espRowIdentity}>
        <Image
          src={ESP_IMAGES_BY_INDEX?.[parseInt(item?.esp)]}
          alt={item?.key || "ESP"}
          className={classes.espRowLogoImg}
        />
        <span className={classes.espRowEmail} title={item?.key}>
          {item?.key}
        </span>
        {renderStatusChip(item)}
      </Box>
      <Box className={classes.espRowLimits}>
        <Box className={classes.espRowLimit}>
          <span className={classes.espRowLimitLabel}>{STRINGS.HOURLY}</span>
          <span className={classes.espRowLimitValue}>
            {item?.hourly_limit ?? 0}
          </span>
        </Box>
        <Box className={classes.espRowDivider} />
        <Box className={classes.espRowLimit}>
          <span className={classes.espRowLimitLabel}>{STRINGS.DAILY}</span>
          <span className={classes.espRowLimitValue}>
            {item?.daily_limit ?? 0}
          </span>
        </Box>
      </Box>
      <Box
        className={classes.espRowKebab}
        onClick={(e) => openOption1(e, item)}
      >
        <IconComponent
          iconLabel={ICON_LABELS.MORE_VERT}
          color={GREY.SECONDARY}
          fontSize={fontSize.MD}
        />
      </Box>
    </Box>
  );

  const optionContent = (
    <div className={classes.optionSty}>
      <div className={classes.optionCont} onClick={openUsage}>
        <IconComponent
          iconLabel={ICON_LABELS.INSIGHTS}
          color={GREY.TERTIARY}
          fontSize={fontSize.SM}
        />
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={STRINGS.VIEW_USAGE}
        />
      </div>
      <div className={classes.optionCont} onClick={openEdit}>
        <IconComponent
          iconLabel={ICON_LABELS.EDIT}
          color={GREY.TERTIARY}
          fontSize={fontSize.SM}
        />
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={STRINGS.EDIT}
        />
      </div>
      {/* A mailbox that cannot send should offer the way back, not an action
          that has already happened. The chip beside it already reads
          "Disconnected" - the menu used to contradict it. */}
      {isRowDisconnected(dataRow) ? (
        <div className={classes.optionCont} onClick={openConnect}>
          <IconComponent
            iconLabel={ICON_LABELS.LINK}
            color={PRIMARY.W_500}
            fontSize={fontSize.SM}
          />
          <TypographyInline
            size={"sm"}
            color={PRIMARY.W_500}
            fontWeight={fontWeight.REGULAR}
            label={STRINGS.CONNECT}
          />
        </div>
      ) : (
        <div className={classes.optionCont} onClick={openDisconnect}>
          <IconComponent
            iconLabel={ICON_LABELS.LINKOFF}
            color={PRIMARY.W_500}
            fontSize={fontSize.SM}
          />
          <TypographyInline
            size={"sm"}
            color={PRIMARY.W_500}
            fontWeight={fontWeight.REGULAR}
            label={STRINGS.DISCONNECT}
          />
        </div>
      )}
    </div>
  );

  const optionEspContent = (
    <div className={classes.optionSty}>
      <div className={classes.optionCont} onClick={openUsage}>
        <IconComponent
          iconLabel={ICON_LABELS.INSIGHTS}
          color={GREY.TERTIARY}
          fontSize={fontSize.SM}
        />
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={STRINGS.VIEW_USAGE}
        />
      </div>
      <div className={classes.optionCont} onClick={openEdit}>
        <IconComponent
          iconLabel={ICON_LABELS.EDIT}
          color={GREY.TERTIARY}
          fontSize={fontSize.SM}
        />
        <TypographyInline
          size={"sm"}
          color={GREY.SECONDARY}
          fontWeight={fontWeight.REGULAR}
          label={STRINGS.EDIT}
        />
      </div>
      <div className={classes.optionCont} onClick={openDisconnect}>
        <IconComponent
          iconLabel={ICON_LABELS.LINKOFF}
          color={PRIMARY.W_500}
          fontSize={fontSize.SM}
        />
        <TypographyInline
          size={"sm"}
          color={PRIMARY.W_500}
          fontWeight={fontWeight.REGULAR}
          label={STRINGS.DISCONNECT}
        />
      </div>
    </div>
  );

  const isEmpty =
    !isSkeleton &&
    !gmailList?.length &&
    !outlookList?.length &&
    !zohoList?.length &&
    !esp?.length;

  // Total connected mailboxes (Gmail + Outlook + Zoho inboxes).
  const mailboxCount =
    (gmailList?.length || 0) +
    (outlookList?.length || 0) +
    (zohoList?.length || 0);
  const mailboxCountLabel =
    mailboxCount === 1
      ? STRINGS.MAILBOX_CONNECTED_ONE
      : STRINGS.MAILBOX_CONNECTED_OTHER;

  return (
    <>
      {isSkeleton ? (
        <Box className={classes.espListSkeleton}>
          {[1, 2, 3].map((n) => (
            <SkeletonLoader
              key={n}
              type="rounded"
              skeletonWidth={"100%"}
              skeletonHeight={80}
            />
          ))}
        </Box>
      ) : isEmpty ? (
        <Box className={classes.espEmpty}>
          <Image
            src={NO_RECORD_FOUND_IMAGE}
            style={{ height: "120px", width: "auto" }}
          />
          <TypographyInline
            color={GREY.PRIMARY}
            fontWeight={fontWeight.SEMIBOLD}
            label={STRINGS.NO_RECORD_FOUND}
            size={"md"}
          />
        </Box>
      ) : (
        <>
          {mailboxCount > 0 && (
            <Box className={classes.espListHeader}>
              <span className={classes.espCountChip}>
                <span className={classes.espCountChipNum}>{mailboxCount}</span>
                {mailboxCountLabel}
              </span>
            </Box>
          )}
          <Box className={classes.espList}>
            {gmailList?.map((item) => renderMailboxRow(item, 2))}
            {outlookList?.map((item) => renderMailboxRow(item, 5))}
            {zohoList?.map((item) => renderMailboxRow(item, 8))}
            {esp?.map((item) => renderOtherEspRow(item))}
          </Box>
        </>
      )}

      <CustomStyledMenu
        width="11vw"
        anchorEl={optionAnchorEl}
        open={optionOpen}
        onClose={handleClose}
      >
        {optionContent}
      </CustomStyledMenu>
      <CustomStyledMenu
        width="11vw"
        anchorEl={optionAnchorEl1}
        open={optionOpen1}
        onClose={handleClose}
      >
        {optionEspContent}
      </CustomStyledMenu>
      {disconnect && (
        <DisconnectModal
          open={disconnect}
          setOpen={setDisconnect}
          reload={reload}
          setReload={setReload}
          setDetails={dataRow}
        />
      )}
      {reconnect && (
        <ReconnectModal
          open={reconnect}
          setOpen={setReconnect}
          mailbox={dataRow}
        />
      )}
      {edit && (
        <EditInboxModal
          open={edit}
          setOpen={setEdit}
          setDetails={dataRow}
          reload={reload}
          setReload={setReload}
        />
      )}
      {usage && (
        <MailboxUsageModal
          open={usage}
          setOpen={setUsage}
          mailbox={dataRow}
          prefetched={bulkUsage[dataRow?.email || dataRow?.key]}
        />
      )}
    </>
  );
};

export default ConnectedEspList;
